import { memo } from 'react'
import type { RemotePresence } from '../collab/usePresence.js'

/**
 * Live-Cursor der anderen Teilnehmer.
 *
 * Die Cursor liegen *innerhalb* des transformierten Layers und werden in
 * Board-Koordinaten positioniert — sie fahren dadurch ohne jede Rechnung im
 * JavaScript korrekt mit Zoom und Pan mit. Damit sie dabei nicht mitskalieren,
 * rechnet der Inhalt über die CSS-Variable `--zoom` gegen. Das läuft komplett
 * im Compositor; ein Zoomvorgang löst hier keinen einzigen Render aus.
 *
 * Wer präsentiert, bekommt statt des Pfeils den Zeiger: einen leuchtenden Punkt
 * ohne Namensschild. Ein Mauszeiger mit Etikett, der über die Folie wandert, ist
 * Werkstattzubehör — im Vortrag zeigt man auf etwas, man arbeitet nicht.
 */
export const Cursors = memo(function Cursors({
  presence,
}: {
  presence: readonly RemotePresence[]
}): React.ReactElement {
  return (
    <>
      {presence.map((remote) => {
        if (!remote.cursor) return null

        if (remote.presenting) {
          // Ohne Farbe kein Zeiger. Der Präsentierende sendet dann ohnehin keine
          // Position mehr; diese Prüfung fängt nur den Moment dazwischen ab.
          if (!remote.pointer) return null
          return (
            <div
              key={remote.clientId}
              className="pointer-beam"
              style={
                {
                  left: `${remote.cursor.x}px`,
                  top: `${remote.cursor.y}px`,
                  '--pointer-color': remote.pointer,
                } as React.CSSProperties
              }
            >
              <span className="pointer-dot" />
            </div>
          )
        }

        return (
          <div
            key={remote.clientId}
            className="cursor"
            style={{ left: `${remote.cursor.x}px`, top: `${remote.cursor.y}px` }}
          >
            <div className="cursor-inner">
              <svg width="18" height="22" viewBox="0 0 18 22" fill="none" aria-hidden="true">
                <path
                  d="M1 1l15.5 8.2-6.9 1.6-3.2 6.4L1 1z"
                  fill={remote.user.color}
                  stroke="rgba(0,0,0,0.35)"
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="cursor-label" style={{ backgroundColor: remote.user.color }}>
                {remote.user.name}
              </span>
            </div>
          </div>
        )
      })}
    </>
  )
})
